/**
 * useJobberJobDetails - Fetches comprehensive job data from jobber_job_details table.
 *
 * This table is populated during gallery image sync and contains:
 * - Financial data (total, invoiced, uninvoiced)
 * - Client and property information
 * - Line items breakdown
 * - Completion date and Jobber web URI
 *
 * @see /apps/production/jobber-image-processor - Worker that populates this data
 * @see /supabase/migrations/20251227163113_create_jobber_job_details.sql
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

/**
 * Client data structure from Jobber
 */
export interface JobberClientData {
  firstName: string
  lastName: string
  emails: string[]
  phones: string[]
}

/**
 * Property/address data from Jobber
 */
export interface JobberPropertyData {
  street1: string
  street2?: string
  city: string
  province: string
  postalCode: string
}

/**
 * Line item from a Jobber job
 */
export interface JobberLineItem {
  id: string
  name: string
  description?: string
  quantity: number
  unitCost: number
  unitPrice?: number
  total?: number
  taxable?: boolean
}

/**
 * Complete job details record from jobber_job_details table
 */
export interface JobberJobDetails {
  id: number
  jobber_job_id: string
  job_number: number | null
  title: string | null
  job_status: string | null
  total_amount: number | null
  invoiced_total: number | null
  uninvoiced_total: number | null
  franchise_region: string
  client_data: JobberClientData | null
  property_data: JobberPropertyData | null
  line_items: JobberLineItem[]
  visits: unknown[] | null
  jobber_created_at: string | null
  completed_at: string | null
  jobber_web_uri: string | null
  last_fetched_at: string | null
  created_at: string | null
  updated_at: string | null
}

/**
 * Fetch job details by job number and optional franchise
 */
async function fetchJobberJobDetails(
  jobNumber: number,
  franchise?: string
): Promise<JobberJobDetails | null> {
  let query = supabase
    .from('jobber_job_details')
    .select('*')
    .eq('job_number', jobNumber)

  // If franchise specified, filter by it
  if (franchise) {
    // Map common franchise names to DB values
    const franchiseMap: Record<string, string> = {
      'north_york': 'northyork',
      'HQ': 'hamilton',
    }
    const dbFranchise = franchiseMap[franchise] ?? franchise
    query = query.eq('franchise_region', dbFranchise)
  }

  const { data, error } = await query.maybeSingle()

  if (error) {
    console.error('Error fetching jobber job details:', error)
    throw error
  }

  return data
}

/**
 * Fetch job details by Jobber job ID (base64 encoded)
 */
async function fetchJobberJobDetailsById(
  jobId: string
): Promise<JobberJobDetails | null> {
  const { data, error } = await supabase
    .from('jobber_job_details')
    .select('*')
    .eq('jobber_job_id', jobId)
    .maybeSingle()

  if (error) {
    console.error('Error fetching jobber job details by ID:', error)
    throw error
  }

  return data
}

/**
 * Hook to fetch job details by job number
 */
export function useJobberJobDetails(
  jobNumber?: number | null,
  franchise?: string
) {
  return useQuery<JobberJobDetails | null>({
    queryKey: ['jobber-job-details', jobNumber, franchise],
    queryFn: () => fetchJobberJobDetails(jobNumber!, franchise),
    enabled: !!jobNumber,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to fetch job details by Jobber job ID
 */
export function useJobberJobDetailsById(jobId?: string | null) {
  return useQuery<JobberJobDetails | null>({
    queryKey: ['jobber-job-details-by-id', jobId],
    queryFn: () => fetchJobberJobDetailsById(jobId!),
    enabled: !!jobId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export default useJobberJobDetails
